import { fetchWorkOrderMaterialCandidates } from '#/api/momMgmt/workOrder/fetchMaterialCandidates';
import { ROUTES } from '#/config/routes';
import { mapCandidateToFormPatch } from '#/modules/momMgmt/workOrder/helper/formSerialize';
import { toast } from '#/utils/toast';

export function parseWorkOrderCreatePrefillParams(searchParams) {
  const manufacturingQty = searchParams.get('manufacturing_qty')?.trim() || '';
  const mtrId = searchParams.get('mtr_id')?.trim() || '';
  const mtrNo = searchParams.get('mtr_no')?.trim() || '';
  const mtrName = searchParams.get('mtr_name')?.trim() || '';
  const fromWorkOrderId = searchParams.get('from_work_order_id')?.trim() || '';

  return {
    manufacturingQty,
    mtrId,
    mtrNo,
    mtrName,
    fromWorkOrderId,
    hasPrefill: Boolean(manufacturingQty || mtrId || mtrNo),
  };
}

function findActiveBomCandidate(rows) {
  return (rows ?? []).find((row) => row?.bom?.status === 'ACTIVE' && row?.mtr?.id);
}

function resolveUnmatchedDisplayName({ mtrName, mtrNo, mtrId, candidateRows }) {
  if (mtrName) {
    return mtrName;
  }
  const fromCandidate = (candidateRows ?? []).find((row) => row?.mtr?.name)?.mtr?.name;
  if (fromCandidate) {
    return String(fromCandidate).trim();
  }
  if (mtrNo) {
    return mtrNo;
  }
  if (mtrId) {
    return mtrId;
  }
  return '';
}

function toastUnmatchedBom(displayName) {
  if (displayName) {
    toast.error(`未匹配到BOM\n名稱：${displayName}`, {
      style: { whiteSpace: 'pre-line' },
    });
    return;
  }
  toast.error('未匹配到BOM');
}

function buildCandidateListQuery({ mtrId, mtrNo }) {
  const filters = {
    bomStatus: ['ACTIVE'],
  };

  if (mtrId) {
    filters.mtrId = mtrId;
  } else if (mtrNo) {
    filters.mtrNo = mtrNo;
  }

  return {
    page: 1,
    pageSize: 20,
    keyword: '',
    sort: [],
    filters,
  };
}

function applyCandidatePatch(setValue, candidate) {
  const patch = mapCandidateToFormPatch(candidate);
  Object.entries(patch).forEach(([key, value]) => {
    setValue(key, value, { shouldDirty: true, shouldValidate: key === 'mtrId' });
  });
  return patch.mtrId ?? '';
}

/**
 * create 模式：讀 URL query → 預填製造量與成品；完成後 replace 清 query。
 * 未匹配 ACTIVE BOM 且有 from_work_order_id → replace 回工單詳情。
 */
export async function prefillWorkOrderFromSearchParams({
  searchParams,
  setValue,
  navigate,
}) {
  const {
    manufacturingQty,
    mtrId,
    mtrNo,
    mtrName,
    fromWorkOrderId,
    hasPrefill,
  } = parseWorkOrderCreatePrefillParams(searchParams);

  if (!hasPrefill) {
    return { prefillMtrId: '' };
  }

  if (manufacturingQty) {
    setValue('manufacturingQty', manufacturingQty, { shouldDirty: true });
  }

  let prefillMtrId = mtrId;

  if (mtrId || mtrNo) {
    try {
      const result = await fetchWorkOrderMaterialCandidates(
        buildCandidateListQuery({ mtrId, mtrNo }),
      );
      const candidate = findActiveBomCandidate(result?.data);

      if (candidate) {
        prefillMtrId = applyCandidatePatch(setValue, candidate);
      } else {
        const displayName = resolveUnmatchedDisplayName({
          mtrName,
          mtrNo,
          mtrId,
          candidateRows: result?.data,
        });
        toastUnmatchedBom(displayName);
        if (fromWorkOrderId) {
          navigate(ROUTES.workOrderDetail(fromWorkOrderId), { replace: true });
          return { prefillMtrId: '' };
        }
      }
    } catch {
      const displayName = resolveUnmatchedDisplayName({
        mtrName,
        mtrNo,
        mtrId,
        candidateRows: [],
      });
      toastUnmatchedBom(displayName);
      if (fromWorkOrderId) {
        navigate(ROUTES.workOrderDetail(fromWorkOrderId), { replace: true });
        return { prefillMtrId: '' };
      }
    }
  }

  navigate(ROUTES.workOrderCreate, { replace: true });

  return { prefillMtrId };
}
